import React, { useState } from 'react';
import {
  X,
  CheckCircle,
  AlertTriangle,
  Shield,
  MinusCircle,
  ChevronDown,
  ChevronUp,
  FileSearch,
} from 'lucide-react';
import AcknowledgeClaimModal from './AcknowledgeClaimModal';
import securityApi from '../../../api/securityApi';
import toast from 'react-hot-toast';

const CATEGORY_LABELS = {
  tool_or_technology: 'Tool/Tech',
  process_or_procedure: 'Process',
  personnel: 'Personnel',
  monitoring: 'Monitoring',
  physical_security: 'Physical',
  encryption: 'Encryption',
  access_control: 'Access Control',
  backup_recovery: 'Backup/Recovery',
  incident_response: 'Incident Response',
  vendor_management: 'Vendor Mgmt',
  training: 'Training',
  other: 'Other',
};

const StatusIcon = ({ status }) => {
  switch (status) {
    case 'verified':
      return <CheckCircle className="w-4 h-4 text-green-500 flex-shrink-0" />;
    case 'unverified':
      return <AlertTriangle className="w-4 h-4 text-red-500 flex-shrink-0" />;
    case 'acknowledged_unverified':
      return <Shield className="w-4 h-4 text-amber-500 flex-shrink-0" />;
    case 'not_applicable':
      return <MinusCircle className="w-4 h-4 text-gray-400 flex-shrink-0" />;
    default:
      return <AlertTriangle className="w-4 h-4 text-red-500 flex-shrink-0" />;
  }
};

const StatusLabel = ({ status }) => {
  const labels = {
    verified: { text: 'Verified', color: 'text-green-700 bg-green-50' },
    unverified: { text: 'Unverified', color: 'text-red-700 bg-red-50' },
    acknowledged_unverified: { text: 'Acknowledged', color: 'text-amber-700 bg-amber-50' },
    not_applicable: { text: 'N/A', color: 'text-gray-600 bg-gray-100' },
  };
  const label = labels[status] || labels.unverified;
  return (
    <span className={`text-xs font-medium px-1.5 py-0.5 rounded ${label.color}`}>
      {label.text}
    </span>
  );
};

const ClaimCard = ({ claim, onAcknowledge, onMarkNA, onRemoveFromPolicy }) => {
  const [expanded, setExpanded] = useState(false);
  const truncated = claim.claim_text.length > 100;
  const displayText = expanded || !truncated
    ? claim.claim_text
    : claim.claim_text.slice(0, 100) + '...';

  return (
    <div className="border border-gray-200 rounded-lg p-3 space-y-2">
      <div className="flex items-start gap-2">
        <StatusIcon status={claim.verification_status} />
        <div className="flex-1 min-w-0">
          <p className="text-sm text-gray-800 leading-snug">{displayText}</p>
          {truncated && (
            <button
              onClick={() => setExpanded(!expanded)}
              className="flex items-center gap-1 text-xs text-teal-600 hover:text-teal-700 mt-1"
            >
              {expanded ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
              {expanded ? 'Show less' : 'Show more'}
            </button>
          )}
        </div>
      </div>

      <div className="flex items-center gap-2 flex-wrap">
        <span className="text-xs font-medium px-1.5 py-0.5 rounded bg-gray-100 text-gray-600">
          {CATEGORY_LABELS[claim.claim_category] || claim.claim_category}
        </span>
        <StatusLabel status={claim.verification_status} />
      </div>

      {claim.verification_status === 'acknowledged_unverified' && claim.justification && (
        <p className="text-xs text-amber-700 bg-amber-50 rounded p-2 italic">
          {claim.justification}
        </p>
      )}

      {claim.verification_status === 'unverified' && (
        <div className="flex items-center gap-2 pt-1">
          <button
            onClick={() => onAcknowledge(claim)}
            className="text-xs font-medium text-amber-700 hover:text-amber-800 bg-amber-50 hover:bg-amber-100 px-2 py-1 rounded border border-amber-200"
          >
            Acknowledge Risk
          </button>
          <button
            onClick={() => onMarkNA(claim)}
            className="text-xs font-medium text-gray-600 hover:text-gray-700 bg-gray-50 hover:bg-gray-100 px-2 py-1 rounded border border-gray-200"
          >
            Mark N/A
          </button>
          <button
            onClick={() => onRemoveFromPolicy(claim)}
            className="text-xs font-medium text-blue-600 hover:text-blue-700 bg-blue-50 hover:bg-blue-100 px-2 py-1 rounded border border-blue-200"
          >
            Find in Policy
          </button>
        </div>
      )}
    </div>
  );
};

const ClaimVerificationSidebar = ({
  projectId,
  policyId,
  claims,
  claimsSummary,
  isOpen,
  onClose,
  onClaimsUpdated,
  onFindInPolicy,
}) => {
  const [acknowledgeModal, setAcknowledgeModal] = useState(null);
  const [updating, setUpdating] = useState(false);

  if (!isOpen) return null;

  const handleAcknowledge = async (justification) => {
    if (!acknowledgeModal) return;
    setUpdating(true);
    try {
      await securityApi.updatePolicyClaim(projectId, policyId, acknowledgeModal.id, {
        verification_status: 'acknowledged_unverified',
        justification,
      });
      toast.success('Claim acknowledged');
      setAcknowledgeModal(null);
      onClaimsUpdated();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to acknowledge claim');
    } finally {
      setUpdating(false);
    }
  };

  const handleMarkNA = async (claim) => {
    setUpdating(true);
    try {
      await securityApi.updatePolicyClaim(projectId, policyId, claim.id, {
        verification_status: 'not_applicable',
      });
      toast.success('Claim marked as not applicable');
      onClaimsUpdated();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update claim');
    } finally {
      setUpdating(false);
    }
  };

  const totalClaims = claimsSummary?.total || 0;
  const verifiedCount = claimsSummary?.verified_count || 0;
  const unverifiedCount = claimsSummary?.unverified_count || 0;

  return (
    <>
      {/* Backdrop */}
      <div className="fixed inset-0 bg-black bg-opacity-30 z-40" onClick={onClose} />

      {/* Sidebar */}
      <div className="fixed inset-y-0 right-0 w-96 bg-white shadow-2xl z-50 flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-gray-200">
          <div className="flex items-center gap-2">
            <FileSearch className="w-5 h-5 text-teal-600" />
            <h2 className="text-lg font-semibold text-gray-900">Claim Verification</h2>
          </div>
          <button onClick={onClose} className="p-1 hover:bg-gray-100 rounded">
            <X className="w-5 h-5 text-gray-400" />
          </button>
        </div>

        {/* Summary */}
        <div className="px-4 py-3 bg-gray-50 border-b border-gray-200">
          <p className="text-sm text-gray-700">
            <span className="font-semibold text-green-700">{verifiedCount}</span> of{' '}
            <span className="font-semibold">{totalClaims}</span> claims verified.
            {unverifiedCount > 0 && (
              <>
                {' '}<span className="font-semibold text-red-600">{unverifiedCount}</span> claims need attention.
              </>
            )}
          </p>
          {totalClaims > 0 && (
            <div className="w-full bg-gray-200 rounded-full h-2 mt-2">
              <div
                className="bg-green-500 h-2 rounded-full transition-all"
                style={{ width: `${((totalClaims - unverifiedCount) / totalClaims) * 100}%` }}
              />
            </div>
          )}
        </div>

        {/* Claims list */}
        <div className="flex-1 overflow-y-auto p-4 space-y-3">
          {(!claims || claims.length === 0) ? (
            <div className="text-center py-8 text-gray-500">
              <FileSearch className="w-8 h-8 mx-auto mb-2 text-gray-300" />
              <p className="text-sm">No claims extracted yet.</p>
              <p className="text-xs mt-1">Claims will be extracted when the policy is generated or saved.</p>
            </div>
          ) : (
            claims.map((claim) => (
              <ClaimCard
                key={claim.id}
                claim={claim}
                onAcknowledge={setAcknowledgeModal}
                onMarkNA={handleMarkNA}
                onRemoveFromPolicy={() => onFindInPolicy(claim.claim_text)}
              />
            ))
          )}
        </div>
      </div>

      {/* Acknowledge Modal */}
      {acknowledgeModal && (
        <AcknowledgeClaimModal
          claim={acknowledgeModal}
          onConfirm={handleAcknowledge}
          onClose={() => setAcknowledgeModal(null)}
          loading={updating}
        />
      )}
    </>
  );
};

export default ClaimVerificationSidebar;
